import { t, type LandingLocale } from '../i18n/I18n.js';
import { escapeAttr } from '../html.js';
import { renderHeader, renderFooter } from './chrome.js';
import { renderThumb, type ThumbParams } from './thumbs.js';
import { renderChangelog } from './changelog.js';
import { SEO_GAMES, gamePath, rulesPath, type SeoGameKey } from '@bn-games/vite-seo/games';

// Screenshot base names — see thumbs.ts for the emitted variant URLs.
export const BACKGAMMON_THUMB = 'backgammon';
export const CHECKERS_THUMB = 'checkers';
export const DOMINO_THUMB = 'domino-1';
export const SUDOKU_THUMB = 'sudoku';
export const MAHJONG_THUMB = 'mahjong';
export const CHESS_THUMB = 'chess';

export function renderPage(locale: LandingLocale): string {
  return `
    <a class="skip-link" href="#main">${t('landing.skipLink')}</a>
    ${renderHeader(locale)}
    <main id="main" tabindex="-1" class="page">
      ${renderHero()}
      ${renderGames(locale)}
      ${renderAbout()}
      ${renderChangelog()}
    </main>
    ${renderFooter(locale)}
  `;
}

function renderHero(): string {
  return `
    <section class="hero" id="top">
      <h1 class="hero-title">${t('hero.title')}</h1>
      <p class="hero-subtitle">${t('hero.subtitle')}</p>
      <p class="hero-subtitle">${t('hero.social')}</p>
    </section>
  `;
}

// Per-card presentation; which games exist, their order and their URLs come
// from SEO_GAMES — the one list every generated page and the sitemap share.
const CARD: Record<SeoGameKey, { readonly thumb: string; readonly badge?: () => string }> = {
  chess: { thumb: CHESS_THUMB, badge: () => t('games.chess.newBadge') },
  mahjong: { thumb: MAHJONG_THUMB },
  sudoku: { thumb: SUDOKU_THUMB },
  domino: { thumb: DOMINO_THUMB },
  checkers: { thumb: CHECKERS_THUMB },
  backgammon: { thumb: BACKGAMMON_THUMB },
};

// The first two cards are above the fold: lazy-loading them left empty frames
// on first paint. Only the first (the LCP candidate) also gets fetchpriority.
function thumbPriority(index: number): ThumbParams['priority'] {
  if (index === 0) return 'high';
  return index === 1 ? 'eager' : undefined;
}

function renderGames(locale: LandingLocale): string {
  const tiles = SEO_GAMES.map((game, i) => gameTile(game.key, game.path, locale, thumbPriority(i))).join('');
  return `
    <section class="section section-games" id="games">
      <h2 class="section-title">${t('games.sectionTitle')}</h2>
      <div class="game-tiles">${tiles}</div>
    </section>
  `;
}

function gameTile(
  key: SeoGameKey,
  path: string,
  locale: LandingLocale,
  priority: ThumbParams['priority'],
): string {
  const card = CARD[key];
  const badge = card.badge === undefined ? '' : `<span class="game-badge">${card.badge()}</span>`;
  const href = gamePath(path, locale);
  const cta = t(`games.${key}.cta` as const);
  return `
    <article class="game-tile game-tile--${key}">
      <a class="game-thumb" href="${href}" aria-label="${escapeAttr(cta)}">
        ${renderThumb({ name: card.thumb, alt: t(`games.${key}.thumbAlt` as const), priority })}
      </a>
      <h3 class="game-name">${t(`games.${key}.name` as const)}${badge}</h3>
      <p class="game-description">${t(`games.${key}.description` as const)}</p>
      <div class="game-actions">
        <a class="cta cta-primary game-cta" href="${href}">${cta}</a>
        <a class="game-rules" href="${rulesPath(path, locale)}">${t('games.rulesLink')}</a>
      </div>
    </article>
  `;
}

function renderAbout(): string {
  return `
    <section class="section section-about" id="about">
      <h2 class="section-title">${t('about.sectionTitle')}</h2>
      <p class="about-body">${t('about.body')}</p>
    </section>
  `;
}

// Re-export so main.ts can keep its import surface unchanged.
export type { LandingLocale };
